/**
 * Base Role Abstract Class
 * Defines the interface for all role-specific behaviors
 */

import { User } from '../User';

export abstract class BaseRole {
  protected user: User;

  constructor(user: User) {
    this.user = user;
  }

  // ===== Abstract Methods - Must be implemented by subclasses =====

  /**
   * Get the role name
   */
  abstract get roleName(): string;

  /**
   * Get the role display name for UI
   */
  abstract get displayName(): string;

  /**
   * Get the default landing page after login
   */
  abstract getDefaultRoute(): string;

  /**
   * Get available navigation items for this role
   */
  abstract getNavigationItems(): NavigationItem[];

  /**
   * Check if this role can perform a specific action
   */
  abstract canPerformAction(action: string): boolean;

  // ===== Common Methods =====

  /**
   * Get role-specific settings
   */
  getSettings(): Record<string, any> {
    return {};
  }

  /**
   * Get role badge color for UI
   */
  getBadgeColor(): string {
    return 'default';
  }

  /**
   * Get role icon name
   */
  getIconName(): string {
    return 'User';
  }

  /**
   * Check if role has access to a specific route
   */
  hasAccessToRoute(route: string): boolean {
    const items = this.getNavigationItems();
    return items.some(item => 
      item.path === route || 
      item.children?.some(child => child.path === route)
    );
  }

  /**
   * Get role-specific welcome message
   */
  getWelcomeMessage(): string {
    return `Welcome back, ${this.user.displayName}!`;
  }

  /**
   * Get quick actions for dashboard
   */
  getQuickActions(): QuickAction[] {
    return [];
  }

  /**
   * Get role-specific metrics for dashboard
   */
  getDashboardMetrics(): DashboardMetric[] {
    return [];
  }

  /**
   * Validate if user meets requirements for this role
   */
  validate(): ValidationResult {
    return { isValid: true };
  }
}

// ===== Type Definitions =====

export interface NavigationItem {
  label: string;
  path: string;
  icon?: string;
  badge?: string | number;
  children?: NavigationItem[];
  requiresPermission?: string;
}

export interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: string;
  action: () => void | string; // Function or route
  variant?: 'default' | 'primary' | 'secondary' | 'danger';
}

export interface DashboardMetric {
  id: string;
  label: string;
  value: string | number;
  change?: number; // Percentage change
  changeLabel?: string;
  icon?: string;
  color?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors?: string[];
  warnings?: string[];
}
